import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { Card, CardContent, CardHeader, Grid, Typography } from '@mui/material';
import { BackButton, CustomButton, InputTextFieldGrid } from 'components';
import { getProveedoresService, updateProviderStatusQueriesService } from 'services/admin/providerService';
import { setCurrentProvider, setProviders } from 'redux/slices/admin/providerSlice';
import { setLoading } from 'redux/slices/uiSlice';
import { errorAlert } from 'helpers/alerts';

// Lo que usa el core de tiempo aire mientras el campo esté vacío.
const DEFAULT_FROM = 50;
const DEFAULT_EVERY = 1;
// Al punto de venta se le responde a los 59.5 s. La última consulta que todavía puede
// aprobar la venta sale medio segundo antes, y con el punto de venta ya contestado se
// pregunta dos veces más.
const LAST_USEFUL_QUERY = 59;
const CLOSE = 59.5;
const AFTER_CLOSE = [59.5, 60];

const styles = {
  hint: { display: 'block', mt: -2 },
  schedule: { mt: 1 },
};

const rawValue = (value) => (value === undefined || value === null ? '' : String(value).trim());

const toField = (value) => (typeof value === 'number' && Number.isFinite(value) ? String(value) : '');

const toPayload = (value) => (rawValue(value) === '' ? null : Number(rawValue(value)));

const fromValid = (value) => {
  const raw = rawValue(value);
  if (raw === '') return true;
  if (!/^\d+(\.\d+)?$/.test(raw)) return 'Escribe un número de segundos';
  if (Number(raw) > LAST_USEFUL_QUERY) return `Tiene que empezar a más tardar en el segundo ${LAST_USEFUL_QUERY}: al punto de venta se le responde a los ${CLOSE}`;
  return true;
};

const everyValid = (value) => {
  const raw = rawValue(value);
  if (raw === '') return true;
  if (!/^\d+(\.\d+)?$/.test(raw)) return 'Escribe un número de segundos';
  if (Number(raw) < 0.5) return 'No se puede consultar más seguido que cada medio segundo';
  if (Number(raw) > LAST_USEFUL_QUERY) return `No puede pasar de ${LAST_USEFUL_QUERY} segundos`;
  return true;
};

// Los segundos en que se va a consultar, con la misma regla que aplica el core.
const buildSchedule = (from, every) => {
  const seconds = [];
  for (let second = from; second < LAST_USEFUL_QUERY; second += every) {
    seconds.push(Math.round(second * 10) / 10);
  }
  seconds.push(LAST_USEFUL_QUERY);
  return seconds;
};

const describeSchedule = (values) => {
  if (fromValid(values?.status_query_from_seconds) !== true || everyValid(values?.status_query_every_seconds) !== true) {
    return 'Corrige los valores para ver en qué segundos se consulta.';
  }
  const from = toPayload(values?.status_query_from_seconds);
  const every = toPayload(values?.status_query_every_seconds);
  const seconds = buildSchedule(from === null ? DEFAULT_FROM : from, every === null ? DEFAULT_EVERY : every);
  const list = seconds.length > 12
    ? `${seconds.slice(0, 4).join(', ')}, … ${seconds.slice(-2).join(', ')} (${seconds.length} consultas)`
    : seconds.join(', ');
  return `Consultas que todavía pueden aprobar la venta, en el segundo: ${list}.`;
};

const ProviderStatusQueriesScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { current } = useSelector((store) => store.providers);
  const { control, handleSubmit, reset } = useForm({
    defaultValues: { status_query_from_seconds: '', status_query_every_seconds: '' }
  });
  const values = useWatch({ control });
  const [provider, setProvider] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const goBack = () => navigate('/admin/proveedores');

  useEffect(() => {
    if (!current?.id) {
      setNotFound(true);
      return;
    }
    getProveedoresService()
      .then((response) => {
        const found = Array.isArray(response) ? response.find((item) => item?.id === current.id) : null;
        if (!found) {
          setNotFound(true);
          return;
        }
        setProvider(found);
        reset({
          status_query_from_seconds: toField(found.status_query_from_seconds),
          status_query_every_seconds: toField(found.status_query_every_seconds),
        });
      })
      .catch((error) => errorAlert(error));
    // eslint-disable-next-line
  }, []);

  const submit = (data) => {
    dispatch(setLoading(true));
    updateProviderStatusQueriesService(provider.id, {
      status_query_from_seconds: toPayload(data.status_query_from_seconds),
      status_query_every_seconds: toPayload(data.status_query_every_seconds),
    })
      .then((response) => {
        if (Array.isArray(response)) {
          dispatch(setProviders(response));
          const updated = response.find((item) => item?.id === provider.id);
          if (updated) dispatch(setCurrentProvider(updated));
        }
        goBack();
      })
      .catch((error) => errorAlert(error))
      .finally(() => dispatch(setLoading(false)));
  };

  if (notFound) {
    return (
      <>
        <BackButton name={'Consultas de estado'} click={goBack} />
        <Grid container justifyContent="center" mt={4}>
          <Typography>No se encontró el proveedor. Vuelve a la lista de proveedores y entra de nuevo.</Typography>
        </Grid>
      </>
    );
  }

  return (
    <>
      <BackButton name={'Consultas de estado'} click={goBack} />
      <Grid container direction="column" alignItems="center" mt={2}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader
                title={provider?.container_name || '-'}
                subheader={'Cuándo se le pregunta el estado de una venta que no ha contestado'}
              />
              <CardContent>
                <Grid container spacing={3}>
                  <InputTextFieldGrid
                    xs={12}
                    name={'status_query_from_seconds'}
                    control={control}
                    label={'Empezar a consultar en el segundo'}
                    rules={{ validate: fromValid }}
                  />
                  <InputTextFieldGrid
                    xs={12}
                    name={'status_query_every_seconds'}
                    control={control}
                    label={'Repetir la consulta cada (segundos)'}
                    rules={{ validate: everyValid }}
                  />
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={styles.hint}>
                      {`Vacío usa el valor por omisión: desde el segundo ${DEFAULT_FROM}, cada ${DEFAULT_EVERY} segundo. Los segundos se cuentan desde que la venta llega al sistema, y el cambio aplica desde la siguiente venta.`}
                    </Typography>
                    <Typography variant="body2" sx={styles.schedule}>
                      {describeSchedule(values)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {`Al punto de venta se le responde a los ${CLOSE} s. Ya con el punto de venta contestado se consulta dos veces más, a los ${AFTER_CLOSE.join(' y a los ')} s, para detectar recargas que el proveedor aplicó tarde.`}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Grid item mt={4}>
          <CustomButton name={'Guardar'} click={handleSubmit(submit)} disabled={!provider} />
        </Grid>
      </Grid>
    </>
  );
};

export default ProviderStatusQueriesScreen;
